// @ts-nocheck
import { debounce } from 'lodash';
import { Mention } from '@tiptap/extension-mention';
import { ReactNodeViewRenderer } from '@tiptap/react';

import REGEX, { createMentionTriggerRegex } from '../../../../../const/Regex';
import renderMentionListForChat from '../utils/renderMentionListForChat';
import {
  UserMentionNodeView,
  ModelMentionNodeView,
  ArtifactMentionNodeView,
  DocumentMentionNodeView,
  AttributeMentionNodeView,
} from '../nodeViews/MentionNodeViews';

const HINT_ITEM = [
  { id: '__hint__', label: 'Type to search...', isHint: true },
];

/**
 * Creates a Tiptap suggestion matcher for colon-based mention triggers.
 *
 * Tiptap's default matcher can reject query letters that are also present in
 * multi-character triggers like `model:`. This matcher escapes the full trigger
 * and uses `createMentionTriggerRegex` so `model:employee` and similar queries
 * remain valid.
 *
 * @param {string} char - Full mention trigger text, for example `model:`.
 * @returns {Function} Tiptap matcher returning active range/query or `null`.
 */
function createFindSuggestionMatch(char) {
  const escapedChar = char.replace(REGEX.SPECIAL_CHARS_ESCAPE, '\\$&');
  const regexp = createMentionTriggerRegex(escapedChar);

  return ({ $position }) => {
    const text = $position.nodeBefore?.textContent ?? '';
    const sliceFrom = $position.pos - text.length;
    const match = text.match(regexp);
    if (!match) return null;

    return {
      range: { from: sliceFrom + match.index, to: $position.pos },
      query: match[2],
      text: match[1] + match[2],
    };
  };
}

/**
 * Builds the shared Tiptap attribute schema for mention nodes.
 *
 * @param {object} extraAttrs - Mention-specific Tiptap attribute definitions.
 * @returns {object} Attribute schema consumed by a Tiptap Mention extension.
 */
function makeAttrs(extraAttrs) {
  return {
    id: {
      default: null,
      parseHTML: (el) => el.getAttribute('data-id'),
      renderHTML: (attrs) => (attrs.id != null ? { 'data-id': attrs.id } : {}),
    },
    label: {
      default: null,
      parseHTML: (el) => el.getAttribute('data-label'),
      renderHTML: (attrs) =>
        attrs.label != null ? { 'data-label': attrs.label } : {},
    },
    ...extraAttrs,
  };
}

/**
 * Creates the local `@user` mention extension.
 *
 * User suggestions are filtered from the supplied `userList`; no remote API is
 * called for this trigger.
 *
 * @param {object} options
 * @param {Array<object>} options.userList - Users available to the editor.
 * @returns {import('@tiptap/core').Extension} Tiptap extension for `@`.
 */
export function createUserMentionExtension({ userList }) {
  return Mention.extend({
    name: 'userMention',

    addAttributes() {
      return makeAttrs({
        name: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-name'),
          renderHTML: (attrs) =>
            attrs.name != null ? { 'data-name': attrs.name } : {},
        },
        designation: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-designation'),
          renderHTML: (attrs) =>
            attrs.designation != null
              ? { 'data-designation': attrs.designation }
              : {},
        },
        role: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-role'),
          renderHTML: (attrs) =>
            attrs.role != null ? { 'data-role': attrs.role } : {},
        },
        user_email: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-user_email'),
          renderHTML: (attrs) =>
            attrs.user_email != null
              ? { 'data-user_email': attrs.user_email }
              : {},
        },
      });
    },

    addNodeView() {
      return ReactNodeViewRenderer(UserMentionNodeView);
    },
  }).configure({
    renderLabel: ({ node }) => `@${node.attrs.label || node.attrs.name || ''}`,
    suggestion: {
      char: '@',
      allowSpaces: false,
      items: ({ query }) => {
        const q = (query || '').toLowerCase();
        const list = Array.isArray(userList) ? userList : [];
        return list
          .filter((u) => {
            const name = u.name || u.Name || u.username || '';
            return !q || name.toLowerCase().includes(q);
          })
          .slice(0, 10)
          .map((u) => {
            return {
              id: u.id || u.username,
              label: u.name || u.Name || u.username || '',
              name: u.name || u.Name || u.username || '',
              designation: u.designation || '',
              role: u.role || '',
              user: u.user || u.User || u.username || '',
              user_email: u.user_email || '',
            };
          });
      },
      render: renderMentionListForChat,
    },
  });
}

/**
 * Creates the remote `model:` mention extension.
 *
 * Empty queries show the shared type-to-search hint. Non-empty queries are
 * debounced and resolved by the backend entity search.
 *
 * @param {object} options
 * @param {Function} options.searchMentionEntities - Mention search API.
 * @param {string} options.entityType - Parent entity type for the API.
 * @param {number|string} options.entityId - Parent entity id for the API.
 * @returns {import('@tiptap/core').Extension} Tiptap extension for `model:`.
 */
export function createModelMentionExtension({
  searchMentionEntities,
  entityType,
  entityId,
}) {
  /**
   * Debounced API search for `model:` suggestions.
   *
   * API failures resolve `[]` so the suggestion popup falls back to "No result"
   * instead of interrupting editor input.
   *
   * @param {string} query - User-entered query after `model:`.
   * @param {Function} resolve - Promise resolver supplied by `items`.
   * @returns {void}
   */
  const debouncedSearch = debounce(async (query, resolve) => {
    try {
      const res = await searchMentionEntities({
        entity_type: entityType,
        entity_id: entityId,
        search_keyword: 'model',
        search_query: query,
      });
      resolve(
        (res?.data || []).map((item) => ({
          id: item.id,
          label: `${item.Unique_id || ''} ${item.Name || ''}`.trim(),
          uniqueId: item.Unique_id,
          name: item.Name,
          entityType: item.Entity_type,
          requestType: item.request_type || '',
        }))
      );
    } catch {
      resolve([]);
    }
  }, 300);

  return Mention.extend({
    name: 'modelMention',

    addAttributes() {
      return makeAttrs({
        uniqueId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-unique-id'),
          renderHTML: (attrs) =>
            attrs.uniqueId != null ? { 'data-unique-id': attrs.uniqueId } : {},
        },
        name: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-name'),
          renderHTML: (attrs) =>
            attrs.name != null ? { 'data-name': attrs.name } : {},
        },
        entityType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-type'),
          renderHTML: (attrs) =>
            attrs.entityType != null
              ? { 'data-entity-type': attrs.entityType }
              : {},
        },
        requestType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-request-type'),
          renderHTML: (attrs) =>
            attrs.requestType != null
              ? { 'data-request-type': attrs.requestType }
              : {},
        },
      });
    },

    addNodeView() {
      return ReactNodeViewRenderer(ModelMentionNodeView);
    },
  }).configure({
    renderLabel: ({ node }) =>
      `${node.attrs.uniqueId || ''} ${node.attrs.name || ''}`.trim(),
    suggestion: {
      char: 'model:',
      allowSpaces: false,
      findSuggestionMatch: createFindSuggestionMatch('model:'),
      items: ({ query }) => {
        if (!query.trim()) return HINT_ITEM;
        return new Promise((resolve) => debouncedSearch(query, resolve));
      },
      render: renderMentionListForChat,
    },
  });
}

/**
 * Creates the remote `artifact:` mention extension.
 *
 * Uses the custom colon matcher and normalizes artifact API results into saved
 * mention attrs.
 *
 * @param {object} options
 * @param {Function} options.searchMentionEntities - Mention search API.
 * @param {string} options.entityType - Parent entity type for the API.
 * @param {number|string} options.entityId - Parent entity id for the API.
 * @returns {import('@tiptap/core').Extension} Tiptap extension for `artifact:`.
 */
export function createArtifactMentionExtension({
  searchMentionEntities,
  entityType,
  entityId,
}) {
  /**
   * Debounced API search for `artifact:` suggestions.
   *
   * Results are normalized to the same attr shape used by saved artifact chips;
   * failures resolve `[]` for the no-result fallback.
   *
   * @param {string} query - User-entered query after `artifact:`.
   * @param {Function} resolve - Promise resolver supplied by `items`.
   * @returns {void}
   */
  const debouncedSearch = debounce(async (query, resolve) => {
    try {
      const res = await searchMentionEntities({
        entity_type: entityType,
        entity_id: entityId,
        search_keyword: 'artifact',
        search_query: query,
      });
      resolve(
        (res?.data || []).map((item) => ({
          id: item.id,
          label: `${item.Unique_id || ''} ${item.Name || ''}`.trim(),
          uniqueId: item.Unique_id,
          name: item.Name,
          entityType: item.Entity_type,
          requestType: item.request_type || '',
        }))
      );
    } catch {
      resolve([]);
    }
  }, 300);

  return Mention.extend({
    name: 'artifactMention',

    addAttributes() {
      return makeAttrs({
        uniqueId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-unique-id'),
          renderHTML: (attrs) =>
            attrs.uniqueId != null ? { 'data-unique-id': attrs.uniqueId } : {},
        },
        name: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-name'),
          renderHTML: (attrs) =>
            attrs.name != null ? { 'data-name': attrs.name } : {},
        },
        entityType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-type'),
          renderHTML: (attrs) =>
            attrs.entityType != null
              ? { 'data-entity-type': attrs.entityType }
              : {},
        },
        requestType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-request-type'),
          renderHTML: (attrs) =>
            attrs.requestType != null
              ? { 'data-request-type': attrs.requestType }
              : {},
        },
      });
    },

    addNodeView() {
      return ReactNodeViewRenderer(ArtifactMentionNodeView);
    },
  }).configure({
    renderLabel: ({ node }) =>
      `${node.attrs.uniqueId || ''} ${node.attrs.name || ''}`.trim(),
    suggestion: {
      char: 'artifact:',
      allowSpaces: false,
      findSuggestionMatch: createFindSuggestionMatch('artifact:'),
      items: ({ query }) => {
        if (!query.trim()) return HINT_ITEM;
        return new Promise((resolve) => debouncedSearch(query, resolve));
      },
      render: renderMentionListForChat,
    },
  });
}

/**
 * Creates the remote `document:` mention extension.
 *
 * Preserves both document id and parent entity context for saved chip preview
 * behavior.
 *
 * @param {object} options
 * @param {Function} options.searchMentionEntities - Mention search API.
 * @param {string} options.entityType - Parent entity type and mapping fallback.
 * @param {number|string} options.entityId - Parent entity id and mapping fallback.
 * @returns {import('@tiptap/core').Extension} Tiptap extension for `document:`.
 */
export function createDocumentMentionExtension({
  searchMentionEntities,
  entityType,
  entityId,
}) {
  /**
   * Debounced API search for `document:` suggestions.
   *
   * Handles PascalCase and snake_case backend keys and resolves `[]` on failure
   * so the menu can show the no-result fallback.
   *
   * @param {string} query - User-entered query after `document:`.
   * @param {Function} resolve - Promise resolver supplied by `items`.
   * @returns {void}
   */
  const debouncedSearch = debounce(async (query, resolve) => {
    try {
      const res = await searchMentionEntities({
        entity_type: entityType,
        entity_id: entityId,
        search_keyword: 'document',
        search_query: query,
      });
      resolve(
        (res?.data || []).map((item) => ({
          id: item.id,
          label: item.Name || item.name || '',
          name: item.Name || item.name || '',
          entityId: item.id,
          entityType: item.Entity_type || item.entity_type || entityType,
          parentEntityId: item.Entity_id || item.entity_id || entityId,
        }))
      );
    } catch {
      resolve([]);
    }
  }, 300);

  return Mention.extend({
    name: 'documentMention',

    addAttributes() {
      return makeAttrs({
        name: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-name'),
          renderHTML: (attrs) =>
            attrs.name != null ? { 'data-name': attrs.name } : {},
        },
        entityId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-id'),
          renderHTML: (attrs) =>
            attrs.entityId != null ? { 'data-entity-id': attrs.entityId } : {},
        },
        entityType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-type'),
          renderHTML: (attrs) =>
            attrs.entityType != null
              ? { 'data-entity-type': attrs.entityType }
              : {},
        },
        parentEntityId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-parent-entity-id'),
          renderHTML: (attrs) =>
            attrs.parentEntityId != null
              ? { 'data-parent-entity-id': attrs.parentEntityId }
              : {},
        },
      });
    },

    addNodeView() {
      return ReactNodeViewRenderer(DocumentMentionNodeView);
    },
  }).configure({
    renderLabel: ({ node }) => node.attrs.name || node.attrs.label || '',
    suggestion: {
      char: 'document:',
      allowSpaces: false,
      findSuggestionMatch: createFindSuggestionMatch('document:'),
      items: ({ query }) => {
        if (!query.trim()) return HINT_ITEM;
        return new Promise((resolve) => debouncedSearch(query, resolve));
      },
      render: renderMentionListForChat,
    },
  });
}

/**
 * Creates the remote `attribute:` mention extension.
 *
 * Keeps `parentEntityId` separate from the attribute id so saved chips can
 * fetch attribute details from the parent entity context.
 *
 * @param {object} options
 * @param {Function} options.searchMentionEntities - Mention search API.
 * @param {string} options.entityType - Parent entity type and mapping fallback.
 * @param {number|string} options.entityId - Parent entity id and mapping fallback.
 * @returns {import('@tiptap/core').Extension} Tiptap extension for `attribute:`.
 */
export function createAttributeMentionExtension({
  searchMentionEntities,
  entityType,
  entityId,
}) {
  /**
   * Debounced API search for `attribute:` suggestions.
   *
   * Handles lower-case and PascalCase backend keys, preserving parent entity id
   * separately from the attribute id. Failures resolve `[]`.
   *
   * @param {string} query - User-entered query after `attribute:`.
   * @param {Function} resolve - Promise resolver supplied by `items`.
   * @returns {void}
   */
  const debouncedSearch = debounce(async (query, resolve) => {
    try {
      const res = await searchMentionEntities({
        entity_type: entityType,
        entity_id: entityId,
        search_keyword: 'attribute',
        search_query: query,
      });
      resolve(
        (res?.data || []).map((item) => ({
          id: item.id,
          label: item.name || item.Name || '',
          name: item.name || item.Name || '',
          entityId: item.id,
          entityType: item.entity_type || item.Entity_type || entityType,
          parentEntityId: item.entity_id || item.Entity_id || entityId,
        }))
      );
    } catch {
      resolve([]);
    }
  }, 300);

  return Mention.extend({
    name: 'attributeMention',

    addAttributes() {
      return makeAttrs({
        name: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-name'),
          renderHTML: (attrs) =>
            attrs.name != null ? { 'data-name': attrs.name } : {},
        },
        entityId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-id'),
          renderHTML: (attrs) =>
            attrs.entityId != null ? { 'data-entity-id': attrs.entityId } : {},
        },
        entityType: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-entity-type'),
          renderHTML: (attrs) => {
            return attrs.entityType != null
              ? { 'data-entity-type': attrs.entityType }
              : {};
          },
        },
        id: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-id'),
          renderHTML: (attrs) => {
            return attrs.id != null ? { 'data-id': attrs.id } : {};
          },
        },
        parentEntityId: {
          default: null,
          parseHTML: (el) => el.getAttribute('data-parent-entity-id'),
          renderHTML: (attrs) =>
            attrs.parentEntityId != null
              ? { 'data-parent-entity-id': attrs.parentEntityId }
              : {},
        },
      });
    },

    addNodeView() {
      return ReactNodeViewRenderer(AttributeMentionNodeView);
    },
  }).configure({
    renderLabel: ({ node }) => node.attrs.name || node.attrs.label || '',
    suggestion: {
      char: 'attribute:',
      allowSpaces: false,
      findSuggestionMatch: createFindSuggestionMatch('attribute:'),
      items: ({ query }) => {
        if (!query.trim()) return HINT_ITEM;
        return new Promise((resolve) => debouncedSearch(query, resolve));
      },
      render: renderMentionListForChat,
    },
  });
}
